import { useState } from "react";
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { JobStatus } from "./JobBoard";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/components/ui/use-toast";
import { Calendar } from "lucide-react";
import { format } from "date-fns";
import { z } from "zod";
import { useAuth } from "@/lib/AuthContext";
import { cn } from "@/lib/utils";

const formSchema = z.object({
  url: z.string().url(),
  company_name: z.string().min(1),
  job_title: z.string().min(1),
  description: z.string(),
  deadline: z.string().optional(),
  status: z.string().default("Interested")
});

interface AddJobDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onJobAdded: (job: any) => void;
  demoMode?: boolean;
  isDarkMode?: boolean;
}

export function AddJobDialog({ 
  open, 
  onOpenChange, 
  onJobAdded,
  demoMode = false,
  isDarkMode = false 
}: AddJobDialogProps) {
  const { t } = useTranslation();
  const [jobUrl, setJobUrl] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [description, setDescription] = useState("");
  const [deadline, setDeadline] = useState("");
  const { toast } = useToast();
  const { user } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const formData = {
      url: jobUrl,
      company_name: companyName,
      job_title: jobTitle,
      description,
      deadline: deadline || undefined,
      status: "Interested" as const
    };

    try {
      const validatedData = formSchema.parse(formData);

      if (demoMode) {
        const demoJob = {
          ...validatedData,
          id: Date.now(),
          date_added: new Date().toISOString().split('T')[0],
          status: "Interested"
        };
        
        onJobAdded(demoJob);
        onOpenChange(false);
        
        // Reset form
        setJobUrl("");
        setCompanyName("");
        setJobTitle("");
        setDescription("");
        setDeadline("");
        
        toast({
          title: t('dashboard.jobAdded'),
          description: t('dashboard.jobAdded'),
        });
        return;
      }

      const { error } = await supabase
        .from('jobs')
        .insert([
          {
            ...validatedData,
            user_id: user?.id,
            date_added: new Date().toISOString().split('T')[0]
          }
        ]);

      if (error) throw error;

      onJobAdded(validatedData);
      onOpenChange(false);
      
      // Reset form
      setJobUrl("");
      setCompanyName("");
      setJobTitle("");
      setDescription("");
      setDeadline("");
      
      toast({
        title: t('dashboard.jobAdded'),
        description: t('dashboard.jobAdded'),
      });
    } catch (error) {
      console.error('Error adding job:', error);
      toast({
        title: t('dashboard.jobAddError'),
        description: error instanceof z.ZodError 
          ? t('addJob.required')
          : t('dashboard.jobAddError'),
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn(
        "sm:max-w-[425px]",
        isDarkMode ? "bg-[#2C2E2D] border-[#818881] text-[#D9D9D9]" : "bg-white"
      )}>
        <DialogHeader>
          <DialogTitle className={isDarkMode ? "text-[#D9D9D9]" : "text-gray-900"}>
            {t('addJob.title')}
          </DialogTitle>
          <DialogDescription className={isDarkMode ? "text-[#818881]" : "text-gray-500"}>
            {t('addJob.title')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="url" className={isDarkMode ? "text-[#D9D9D9]" : ""}>{t('addJob.jobUrl')}</Label>
              <Input
                id="url"
                value={jobUrl}
                onChange={(e) => setJobUrl(e.target.value)}
                placeholder="https://..."
                required
                className={isDarkMode ? "bg-[#818881]/20 border-[#818881] text-[#D9D9D9] placeholder:text-[#818881]" : ""}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="company" className={isDarkMode ? "text-[#D9D9D9]" : ""}>{t('addJob.companyName')}</Label>
              <Input
                id="company"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                required
                className={isDarkMode ? "bg-[#818881]/20 border-[#818881] text-[#D9D9D9]" : ""}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="title" className={isDarkMode ? "text-[#D9D9D9]" : ""}>{t('addJob.jobTitle')}</Label>
              <Input
                id="title"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                required
                className={isDarkMode ? "bg-[#818881]/20 border-[#818881] text-[#D9D9D9]" : ""}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="description" className={isDarkMode ? "text-[#D9D9D9]" : ""}>{t('addJob.notes')}</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className={isDarkMode ? "bg-[#818881]/20 border-[#818881] text-[#D9D9D9]" : ""}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="deadline" className={isDarkMode ? "text-[#D9D9D9]" : ""}>Application Deadline</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="deadline"
                  type="date"
                  value={deadline}
                  onChange={(e) => setDeadline(e.target.value)}
                  className={cn("pl-10", isDarkMode ? "bg-[#818881]/20 border-[#818881] text-[#D9D9D9]" : "")}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button 
              type="submit"
              className={isDarkMode ? "bg-[#CCF751] text-[#2C2E2D] hover:bg-[#CCF751]/90" : ""}
            >
              {t('addJob.addJob')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}